import * as React from 'react';
import SelectComponent from 'react-select';

import {
  defaultClassNames,
  defaultStyles,
  isNodeInsideDismissableModal,
  resolveSelectMenuPortalTarget,
  withSelectMenuLayerStyles,
} from './helper';
import {
  ClearIndicator,
  DropdownIndicator,
  Menu,
  MenuList,
  MultiValueRemove,
  Option,
} from './react-select-custom-components';

type SharedSelectProps = React.ComponentPropsWithoutRef<
  typeof SelectComponent
> & {
  /** Menu list shows a trailing loading row while the next page is fetching. */
  isMenuListFetchingMore?: boolean;
  /**
   * When true (default outside dialogs), portal the menu to document.body so
   * sticky table headers do not cover it. Forced off inside Radix dialogs
   * unless `menuPortalTarget` is set explicitly (ENR-4012).
   */
  menuInPortal?: boolean;
};

const Select = React.forwardRef<
  React.ElementRef<typeof SelectComponent>,
  SharedSelectProps
>((props, ref) => {
  const {
    value,
    onChange,
    options = [],
    styles = defaultStyles,
    classNames = defaultClassNames,
    components = {},
    menuPortalTarget,
    menuPosition,
    menuInPortal,
    ...rest
  } = props;

  const id = React.useId();
  const hostRef = React.useRef<HTMLDivElement>(null);
  const [isInsideDialog, setIsInsideDialog] = React.useState(false);

  React.useLayoutEffect(() => {
    setIsInsideDialog(isNodeInsideDismissableModal(hostRef.current));
  }, []);

  const portalTarget = resolveSelectMenuPortalTarget({
    menuPortalTarget,
    menuInPortal,
    isInsideDialog,
  });

  return (
    <div ref={hostRef} className='contents'>
      <SelectComponent
        instanceId={id}
        ref={ref}
        value={value}
        onChange={onChange}
        options={options}
        unstyled
        components={{
          DropdownIndicator,
          ClearIndicator,
          MultiValueRemove,
          Option,
          Menu,
          MenuList,
          ...components,
        }}
        styles={withSelectMenuLayerStyles(styles)}
        classNames={classNames}
        menuPortalTarget={portalTarget}
        menuPosition={menuPosition ?? (portalTarget ? 'fixed' : undefined)}
        {...rest}
      />
    </div>
  );
});
Select.displayName = 'Select';
export default Select;
